import { FC, useMemo } from 'react';
import { Control, useController } from 'react-hook-form';

import { ChevronDownIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../../../components/combobox/index';
import Loader from '../../../../../../components/loader/index';
import { Tooltip } from '../../../../../../components/tooltip/index';
import { useLocale } from '../../../../../../hooks/index';
import { DocumentSubCategory } from '../../../../enums/document-category.const';
import {
  LinkedTargetOption,
  useLinkedTargetOptions,
} from '../../../hooks/use-linked-target-options.hook';
import {
  LINKED_TARGET_NONE_VALUE,
  QuickSignFormSchema,
} from '../../../validation/quick-sign.validation';

export type LinkedEventComboboxProps = {
  companyId: string;
  control: Control<QuickSignFormSchema>;
  disabled?: boolean;
  subCategory: DocumentSubCategory;
};

export const LinkedEventCombobox: FC<LinkedEventComboboxProps> = ({
  companyId,
  control,
  disabled = false,
  subCategory,
}) => {
  const { messagesLocale } = useLocale();
  const { options, isLoading } = useLinkedTargetOptions(companyId, subCategory);

  const { field } = useController({ control, name: 'stepOne.linkedTargetId' });

  const selectedLabel = useMemo(() => {
    if (field.value === LINKED_TARGET_NONE_VALUE) {
      return messagesLocale.NO_LINKED_EVENT;
    }
    if (!field.value) return '';
    const match = options.find((option: LinkedTargetOption) => option.id === field.value);
    return match?.label ?? '';
  }, [field.value, messagesLocale, options]);

  return (
    <FormCombobox className="relative" control={control} name="stepOne.linkedTargetId">
      <div className="relative">
        <FormComboboxInput
          control={control}
          customValue={() => selectedLabel}
          disabled={disabled}
          icon={<ChevronDownIcon className="mt-3 h-4 w-4 text-gray-500" />}
          name="stepOne.linkedTargetId"
          placeholder={<AppFormattedMessage id="LINKED_EVENT_OPTIONAL" />}
          readOnly
          wrapperClassName="min-h-[56px]"
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" disabled={disabled} />
      </div>

      <ComboboxOptions className="absolute z-20 max-h-60 w-full overflow-y-auto rounded border border-gray-200 bg-white p-2 shadow-lg">
        {isLoading && (
          <div className="flex w-full justify-center py-2">
            <Loader />
          </div>
        )}
        {!isLoading && (
          <FormComboboxOption
            control={control}
            name="stepOne.linkedTargetId"
            value={LINKED_TARGET_NONE_VALUE}
          >
            <span className="truncate text-sm font-normal text-gray-700">
              <AppFormattedMessage id="NO_LINKED_EVENT" />
            </span>
          </FormComboboxOption>
        )}
        {!isLoading && options.length === 0 && (
          <p className="px-2 py-1 text-sm text-gray-500">
            <AppFormattedMessage id="NO_RESULTS_FOUND" />
          </p>
        )}
        {!isLoading &&
          options.map((option: LinkedTargetOption) => (
            <FormComboboxOption
              control={control}
              key={option.id}
              name="stepOne.linkedTargetId"
              value={option.id}
            >
              <Tooltip content={option.label}>
                <div className="cursor-pointer truncate text-sm font-normal text-gray-700">
                  {option.label}
                </div>
              </Tooltip>
            </FormComboboxOption>
          ))}
      </ComboboxOptions>
    </FormCombobox>
  );
};
